import { Document } from 'mongoose';
import { AffiliateRecurringType } from '../affiliation';
import { Application } from '../application-config/application-config';
import { OptionalAssets } from '../assets/assets';
import { TimeIdentifiers, UserIdentifier } from '../common/identifiers';
import { CouponItemType } from '../coupon/coupon';
import { GroupType } from '../group/group';
import { CurrencyRates, SupportedCurrencies } from '../payment';
import { ProductType } from '../product/product';
import { Program } from '../program/program';
import { TradingSystem } from '../trading-system';
import { UserInformation } from '../user/user';
import { UserCoupon } from '../user-coupon/user-coupon';

export interface CartEntity extends UserIdentifier {
  currency: SupportedCurrencies;
  step: CartStep;
  billingInformation?: BillingInformation;
  [OptionalAssets.Forex]?: CartAssetEntity;
  [OptionalAssets.Futures]?: CartAssetEntity;
  [OptionalAssets.DayTrade]?: CartAssetEntity;
  [OptionalAssets.Swing]?: CartAssetEntity;
  [OptionalAssets.QuickBuy]?: CartAssetEntity;
}

export type CartType = CartEntity & TimeIdentifiers;

export type CartDocument = CartType & Document;

/**
 * Cart-asset level computed values - aggregated from all items in the cart asset
 */
export interface CartAssetComputed {
  /**
   * Whether credits can be applied to this cart asset.
   * Derived from ALL items - false if ANY item blocks credits.
   */
  enableCreditsUsage: boolean;

  /**
   * Whether the user can pay for the entire cart asset with hub credits.
   * True if user has enough credits to cover the "real price" (considering blocked discounts).
   */
  canPayWithHubCredits: boolean;

  /**
   * Aggregated warning flags - true if ANY item has the respective conflict
   */
  warnings: {
    /** Any program discount ignored due to coupon */
    programDiscountWithCoupon: boolean;
    /** Any program discount ignored due to credits */
    programDiscountWithCredits: boolean;
    /** Coupon + credits stacking warning */
    couponsWithCredits: boolean;
    /** Any program discount + affiliate conflict */
    programDiscountWithAffiliateDiscount: boolean;
  };

  /**
   * Total discount percentage based on full price (including trading system fees)
   * Calculated as: (totalDiscount / fullPrice) * 100, rounded and capped at 100%
   */
  totalDiscountPercentage: number;
}

/**
 * Per-item computed values - specific to each Program-type cart item
 */
export interface CartItemComputed {
  /** Whether this item's program discount is currently applied */
  showProgramDiscount: boolean;
  /** Item price after program discount (original price for strikethrough display) */
  priceAfterProgramDiscount: number;
  /** Title of the program discount if shown */
  discountTitle?: string;
}

export interface CartItemEntity<T extends ProductType = ProductType> {
  productId: string;
  productType: T;
  quantity: number;
  unitPrice: number;
  metadata: ProductMetadataMap[T];
  /** Computed values for Program-type items */
  computed?: T extends ProductType.Program ? CartItemComputed : never;
}

export type ProductMetadataMap = {
  [ProductType.Program]: ProgramMetadata;
  [ProductType.AddOn]: AddOnMetadata;
  [ProductType.Credit]: never;
  [ProductType.General]: never;
};

export interface ProgramMetadata {
  productName?: string;
  groupName?: string;
  tradingSystem: TradingSystem;
  tradingSystemFee?: number;
  /**
   * Program buying power from Program.config.buyingPower
   */
  buyingPower?: number;
  /**
   * Program USD price from Program.price
   */
  priceUsd?: number;
  discount?: {
    title: string;
    percentage: number;
    /**
     * Allow program discount to be applied when credits are used.
     * Default: false (credits override program discount)
     */
    allowWithCredits?: boolean;
    /**
     * Allow program discount to stack with affiliate discount.
     * Default: true (affiliate discount stacks with program discount)
     */
    allowWithAffiliateDiscount?: boolean;
    /**
     * Allow program discount to be applied when a coupon is used.
     * Default: false (coupons override program discount)
     */
    allowWithCoupon?: boolean;
  };
  /**
   * Whether credits usage is blocked for this program.
   * Copied from Program.config.blockCreditsUsage
   */
  blockCreditsUsage?: boolean;
}

export interface AddOnMetadata {
  productName?: string;
  sourceId: string;
}

type AnyDto = {
  productType: ProductType;
  metadata: ProductMetadataMap[keyof ProductMetadataMap];
};
type MetadataFor<K extends ProductType> = {
  metadata: ProductMetadataMap[K];
};

export function isMetadataFromType<K extends ProductType, T extends AnyDto>(
  dto: T,
  kind: K,
): dto is T & { productType: K } & MetadataFor<K> {
  return dto.productType === kind;
}

export interface CartAssetEntity {
  totalPrice: number;
  originalPrice: number;
  totalDiscount?: number;
  items: CartItemEntity[];
  discountMetadata?: DiscountMetadata;
  synced: boolean;
  /** Computed values aggregated from all items in this cart asset */
  computed?: CartAssetComputed;
}

export type DiscountMetadata = Partial<{
  credits: CreditsUsage;
  affiliationDiscount: AffiliationDiscountType;
  couponDiscount: CouponDiscount;
}>;

export type CreditsUsage = {
  creditsUsed: number;
  availableCredits: number;
};

export type CouponDiscount = {
  couponId: string;
  couponType: ProductType;
  couponName: string;

  discountMetadata: {
    discountType: CouponItemType;
    amount: number;
    expirationDate: Date;
  };
  allowWithCredits?: boolean;
};

export type AffiliationDiscountType = {
  affiliateId: string;
  affiliateType: AffiliateRecurringType;

  discountMetadata: {
    amount: number;
  };
};

export enum CartStep {
  Assets = 'assets',
  Programs = 'programs',
  Billing = 'billing',
}

export type BillingInformation = BillingInfo & {
  billingAddress: BillingAddress;
};

export type BillingInfo = Pick<
  UserInformation,
  'email' | 'firstName' | 'lastName' | 'phoneNumber' | 'countryCode'
>;

export type BillingAddress = Pick<
  UserInformation,
  'address' | 'city' | 'zipCode' | 'country' | 'state'
>;

export enum CartDiscountType {
  Coupon = 'coupon',
  Affiliation = 'affiliation',
}

export type CartDiscounts = {
  coupons: UserCoupon[];
  affiliationDiscount: AffiliationDiscountType;
};

export interface CartData {
  cart?: CartType;
  products?: { groups: GroupType[]; programs: Program[] };
  currencies?: CurrencyRates;
  project?: Application;
}
